import React, { useEffect, useState, useRef } from 'react';
import { ShieldCheck, Zap, MessageCircle, Lock, Check, ChevronDown, ChevronUp, ArrowRight, Clock, Globe } from 'lucide-react';
import { WhatCanWeSetupSection, categoryIcons } from '../components/WhatCanWeSetupSection';
type Language = 'en' | 'de' | 'es' | 'he';
const translations = {
  en: {
    hero: {
      headline: 'Stop overpaying for digital services',
      subheadline: 'Private access, fast setup, and personal support — without public pricing.',
      cta: 'Get private access on Telegram',
      note: 'Direct communication. No bots.',
      servicesNote: 'Access to premium digital services at private pricing'
    },
    whatThisIs: {
      title: 'What this is',
      content: 'I help individuals get access to digital services through private sourcing, including setup assistance and support, at prices lower than standard retail. Everything is handled personally and privately via Telegram.'
    },
    whatCanWeSetup: {
      title: 'What can we set up for you',
      categories: [{
        title: 'Software',
        subtitle: 'Apps, subscriptions, setup support'
      }, {
        title: 'AI Tools',
        subtitle: 'Premium tools, setup, guidance'
      }, {
        title: 'Games',
        subtitle: 'Access, setup, in-game assistance'
      }, {
        title: 'Account Setup',
        subtitle: 'Secure setup + handover'
      }],
      ctaText: "Can't find what you're looking for?",
      ctaLink: 'Ask us anything or join our community on Telegram'
    },
    howItWorks: {
      title: 'How it works',
      steps: [{
        title: 'Contact via Telegram',
        desc: 'Join our Telegram group or message us directly. All communication happens there.'
      }, {
        title: 'Request your service',
        desc: 'Tell us what you need and the duration. We confirm availability, price, and timing.'
      }, {
        title: 'Setup & assistance',
        desc: 'We handle the setup, send secure login details, and support you until everything works.'
      }]
    },
    whatYouGet: {
      title: 'What you get',
      subtitle: 'Stop paying retail prices for services that can be sourced more efficiently.',
      items: ['Private pricing (below public retail)', 'Fast delivery (usually same day)', 'Setup assistance included', 'Direct communication (no bots)', '14-day money-back guarantee'],
      guaranteeTitle: 'Ironclad Guarantee',
      guaranteeText: 'Your satisfaction matters more than one transaction. If any issue arises within 14 days and cannot be resolved, you receive a full refund.',
      guaranteeNote: 'No questions asked'
    },
    whyPrivate: {
      title: 'Why private access?',
      content: 'Pay less. Fully private. Deal with real people.',
      benefits: [{
        title: 'Cheaper & faster',
        desc: 'Better pricing than retail. Setups done fast.'
      }, {
        title: 'Private & discreet',
        desc: 'No ID. No credit cards. No exposure.'
      }, {
        title: 'Real human support',
        desc: 'No bots. No tickets. Direct help until it works.'
      }]
    },
    guarantee: {
      title: '14-Day Money-Back Guarantee',
      content: 'If any issue arises within 14 days of delivery and it cannot be resolved, you receive a full refund — no arguments.'
    },
    testimonials: {
      title: 'Real client feedback',
      subtitle: 'Shared with permission. Names blurred for privacy.',
      reviews: [{
        text: 'Setup was done in under 30 minutes. Everything worked exactly as promised.',
        date: 'March 2024'
      }, {
        text: 'Saved me a lot compared to retail. Support was direct and fast.',
        date: 'February 2024'
      }, {
        text: 'Account delivered, password changed, 2FA enabled. Smooth process.',
        date: 'March 2024'
      }, {
        text: 'I was skeptical at first, but it worked perfectly.',
        date: 'January 2024'
      }, {
        text: 'Fast response time and clear instructions. Would use again.',
        date: 'February 2024'
      }, {
        text: 'No hassle, no delays. Exactly what I needed.',
        date: 'March 2024'
      }],
      clientLabel: 'Telegram client',
      disclaimer: 'Reviews are shared with permission. Personal details removed for privacy.'
    },
    faq: {
      title: 'Frequently Asked Questions',
      items: [{
        q: 'How does the service work?',
        a: 'You contact us on Telegram, tell us which service you need and the duration. We confirm availability and price, receive payment, complete the setup, and deliver access with support.'
      }, {
        q: 'What payment methods do you accept?',
        a: 'Currently, we accept crypto payments only. Crypto is fast, secure, and allows instant processing. Additional payment options such as PayPal, cards, and bank transfers are planned and will be added soon.'
      }, {
        q: 'How fast will I receive my account or setup?',
        a: 'In most cases, delivery takes 10 minutes to 1 hour. In rare situations (availability or verification delays), delivery can take up to 24 hours maximum.'
      }, {
        q: 'How do I receive the account details?',
        a: 'Once setup is complete, we send you the username and password via a secure Telegram message. You log in, change the password, enable 2FA, and the account becomes fully yours to use.'
      }, {
        q: 'Will the account be fully under my control?',
        a: 'Yes. After first login, you can change credentials, activate security features, and use the account normally according to the plan you purchased.'
      }, {
        q: 'Is this service official or affiliated with the brands?',
        a: 'No. This is a third-party setup and access assistance service. We are not affiliated with or endorsed by any brand or platform.'
      }, {
        q: 'Is my privacy protected?',
        a: 'Yes. Communication is handled privately via Telegram, and only the minimum information required for setup is requested. No unnecessary data is stored or shared.'
      }, {
        q: 'What happens if there is a problem with my account?',
        a: 'If any issue occurs, we provide direct support. If the issue cannot be resolved within 14 days, you are eligible for a full refund under our money-back guarantee.'
      }, {
        q: 'Are there any recurring charges from your side?',
        a: 'No. We do not charge recurring fees. Any subscription or plan cost is related only to the service you requested and agreed upon.'
      }, {
        q: 'Why are prices not listed publicly?',
        a: 'Pricing and availability can change depending on the service and duration. To ensure accuracy and fair access, all quotes are provided privately via Telegram.'
      }]
    },
    finalCta: {
      title: 'Ready to stop overpaying?',
      button: 'Get private access on Telegram'
    },
    footer: {
      disclaimer: 'This is a third-party digital services assistance platform. Not affiliated with any software or service providers. All services are provided privately with setup support and a 14-day guarantee.',
      copyright: 'Private Digital Concierge. All rights reserved.'
    }
  },
  de: {
    hero: {
      headline: 'Hören Sie auf, für digitale Dienste zu viel zu bezahlen',
      subheadline: 'Privater Zugang, schnelle Einrichtung und persönlicher Support — ohne öffentliche Preise.',
      cta: 'Privaten Zugang auf Telegram erhalten',
      note: 'Direkte Kommunikation. Keine Bots.',
      servicesNote: 'Zugang zu Premium-Digitaldiensten zu privaten Preisen'
    },
    whatThisIs: {
      title: 'Was ist das',
      content: 'Ich helfe Einzelpersonen, Zugang zu digitalen Diensten durch private Beschaffung zu erhalten, einschließlich Einrichtungshilfe und Support, zu Preisen unter dem Standard-Einzelhandel. Alles wird persönlich und privat über Telegram abgewickelt.'
    },
    whatCanWeSetup: {
      title: 'Was können wir für Sie einrichten',
      categories: [{
        title: 'Software',
        subtitle: 'Apps, Abonnements, Einrichtungssupport'
      }, {
        title: 'KI-Tools',
        subtitle: 'Premium-Tools, Einrichtung, Anleitung'
      }, {
        title: 'Spiele',
        subtitle: 'Zugang, Einrichtung, In-Game-Hilfe'
      }, {
        title: 'Konto-Einrichtung',
        subtitle: 'Sichere Einrichtung + Übergabe'
      }],
      ctaText: 'Finden Sie nicht, was Sie suchen?',
      ctaLink: 'Fragen Sie uns oder treten Sie unserer Community auf Telegram bei'
    },
    howItWorks: {
      title: 'So funktioniert es',
      steps: [{
        title: 'Kontakt über Telegram',
        desc: 'Treten Sie unserer Telegram-Gruppe bei oder schreiben Sie uns direkt. Die gesamte Kommunikation findet dort statt.'
      }, {
        title: 'Service anfragen',
        desc: 'Sagen Sie uns, was Sie brauchen und für welche Dauer. Wir bestätigen Verfügbarkeit, Preis und Zeitplan.'
      }, {
        title: 'Einrichtung & Unterstützung',
        desc: 'Wir kümmern uns um die Einrichtung, senden sichere Anmeldedaten und unterstützen Sie, bis alles funktioniert.'
      }]
    },
    whatYouGet: {
      title: 'Was Sie erhalten',
      subtitle: 'Hören Sie auf, Einzelhandelspreise für Dienste zu zahlen, die effizienter beschafft werden können.',
      items: ['Private Preise (unter öffentlichem Einzelhandel)', 'Schnelle Lieferung (normalerweise am selben Tag)', 'Einrichtungshilfe inklusive', 'Direkte Kommunikation (keine Bots)', '14-Tage-Geld-zurück-Garantie'],
      guaranteeTitle: 'Eiserne Garantie',
      guaranteeText: 'Ihre Zufriedenheit ist wichtiger als eine Transaktion. Wenn innerhalb von 14 Tagen ein Problem auftritt und nicht gelöst werden kann, erhalten Sie eine vollständige Rückerstattung.',
      guaranteeNote: 'Keine Fragen gestellt'
    },
    whyPrivate: {
      title: 'Warum privater Zugang?',
      content: 'Weniger zahlen. Völlig privat. Mit echten Menschen.',
      benefits: [{
        title: 'Günstiger & schneller',
        desc: 'Bessere Preise als Einzelhandel. Schnelle Einrichtung.'
      }, {
        title: 'Privat & diskret',
        desc: 'Kein Ausweis. Keine Kreditkarten. Keine Offenlegung.'
      }, {
        title: 'Echter menschlicher Support',
        desc: 'Keine Bots. Keine Tickets. Direkte Hilfe bis es funktioniert.'
      }]
    },
    guarantee: {
      title: '14-Tage-Geld-zurück-Garantie',
      content: 'Wenn innerhalb von 14 Tagen nach Lieferung ein Problem auftritt und nicht gelöst werden kann, erhalten Sie eine vollständige Rückerstattung — ohne Diskussion.'
    },
    testimonials: {
      title: 'Echtes Kundenfeedback',
      subtitle: 'Mit Erlaubnis geteilt. Namen aus Datenschutzgründen unkenntlich gemacht.',
      reviews: [{
        text: 'Einrichtung in unter 30 Minuten erledigt. Alles funktionierte genau wie versprochen.',
        date: 'März 2024'
      }, {
        text: 'Viel günstiger als Einzelhandel. Support war direkt und schnell.',
        date: 'Februar 2024'
      }, {
        text: 'Konto geliefert, Passwort geändert, 2FA aktiviert. Reibungsloser Ablauf.',
        date: 'März 2024'
      }, {
        text: 'Ich war anfangs skeptisch, aber es funktionierte perfekt.',
        date: 'Januar 2024'
      }, {
        text: 'Schnelle Reaktionszeit und klare Anweisungen. Würde wieder nutzen.',
        date: 'Februar 2024'
      }, {
        text: 'Kein Ärger, keine Verzögerungen. Genau das, was ich brauchte.',
        date: 'März 2024'
      }],
      clientLabel: 'Telegram-Kunde',
      disclaimer: 'Bewertungen werden mit Erlaubnis geteilt. Persönliche Details aus Datenschutzgründen entfernt.'
    },
    faq: {
      title: 'Häufig gestellte Fragen',
      items: [{
        q: 'Wie funktioniert der Service?',
        a: 'Sie kontaktieren uns auf Telegram, teilen uns mit, welchen Service Sie benötigen und für welche Dauer. Wir bestätigen Verfügbarkeit und Preis, erhalten die Zahlung, führen die Einrichtung durch und liefern den Zugang mit Support.'
      }, {
        q: 'Welche Zahlungsmethoden akzeptieren Sie?',
        a: 'Derzeit akzeptieren wir nur Krypto-Zahlungen. Krypto ist schnell, sicher und ermöglicht sofortigen Verarbeitung. Zusätzliche Zahlungsoptionen wie PayPal, Karten und Banküberweisungen sind geplant und werden bald hinzugefügt.'
      }, {
        q: 'Wie schnell erhalte ich mein Konto oder Setup?',
        a: 'In den meisten Fällen dauert die Lieferung 10 Minuten bis 1 Stunde. In seltenen Situationen (Verfügbarkeits- oder Verifizierungsverzögerungen) kann die Lieferung maximal bis zu 24 Stunden dauern.'
      }, {
        q: 'Wie erhalte ich die Kontodaten?',
        a: 'Sobald die Einrichtung abgeschlossen ist, senden wir Ihnen Benutzernamen und Passwort über eine sichere Telegram-Nachricht. Sie melden sich an, ändern das Passwort, aktivieren 2FA, und das Konto gehört vollständig Ihnen.'
      }, {
        q: 'Wird das Konto vollständig unter meiner Kontrolle sein?',
        a: 'Ja. Nach der ersten Anmeldung können Sie Anmeldedaten ändern, Sicherheitsfunktionen aktivieren und das Konto normal gemäß dem von Ihnen erworbenen Plan nutzen.'
      }, {
        q: 'Ist dieser Service offiziell oder mit den Marken verbunden?',
        a: 'Nein. Dies ist ein Drittanbieter-Einrichtungs- und Zugangshilfe-Service. Wir sind nicht mit einer Marke oder Plattform verbunden oder von ihr unterstützt.'
      }, {
        q: 'Ist meine Privatsphäre geschützt?',
        a: 'Ja. Die Kommunikation erfolgt privat über Telegram, und es werden nur die für die Einrichtung erforderlichen Mindestinformationen angefordert. Keine unnötigen Daten werden gespeichert oder weitergegeben.'
      }, {
        q: 'Was passiert, wenn es ein Problem mit meinem Konto gibt?',
        a: 'Wenn ein Problem auftritt, bieten wir direkten Support. Wenn das Problem nicht innerhalb von 14 Tagen gelöst werden kann, haben Sie Anspruch auf eine vollständige Rückerstattung gemäß unserer Geld-zurück-Garantie.'
      }, {
        q: 'Gibt es wiederkehrende Gebühren von Ihrer Seite?',
        a: 'Nein. Wir erheben keine wiederkehrenden Gebühren. Alle Abonnement- oder Plankosten beziehen sich nur auf den von Ihnen angeforderten und vereinbarten Service.'
      }, {
        q: 'Warum sind die Preise nicht öffentlich aufgeführt?',
        a: 'Preise und Verfügbarkeit können je nach Service und Dauer variieren. Um Genauigkeit und fairen Zugang zu gewährleisten, werden alle Angebote privat über Telegram bereitgestellt.'
      }]
    },
    finalCta: {
      title: 'Bereit, nicht mehr zu viel zu bezahlen?',
      button: 'Privaten Zugang auf Telegram erhalten'
    },
    footer: {
      disclaimer: 'Dies ist eine Drittanbieter-Plattform für digitale Dienstleistungen. Keine Verbindung zu Software- oder Dienstleistern. Alle Dienste werden privat mit Einrichtungssupport und 14-Tage-Garantie bereitgestellt.',
      copyright: 'Private Digital Concierge. Alle Rechte vorbehalten.'
    }
  },
  es: {
    hero: {
      headline: 'Deja de pagar de más por servicios digitales',
      subheadline: 'Acceso privado, configuración rápida y soporte personal — sin precios públicos.',
      cta: 'Obtener acceso privado en Telegram',
      note: 'Comunicación directa. Sin bots.',
      servicesNote: 'Acceso a servicios digitales premium a precios privados'
    },
    whatThisIs: {
      title: 'Qué es esto',
      content: 'Ayudo a personas a obtener acceso a servicios digitales mediante adquisición privada, incluyendo asistencia de configuración y soporte, a precios más bajos que el comercio minorista estandar. Todo se maneja personal y privadamente a través de Telegram.'
    },
    whatCanWeSetup: {
      title: 'Qué podemos configurar para ti',
      categories: [{
        title: 'Software',
        subtitle: 'Apps, suscripciones, soporte de configuración'
      }, {
        title: 'Herramientas IA',
        subtitle: 'Herramientas premium, configuración, guía'
      }, {
        title: 'Juegos',
        subtitle: 'Acceso, configuración, asistencia en juego'
      }, {
        title: 'Configuración de Cuenta',
        subtitle: 'Configuración segura + entrega'
      }],
      ctaText: '¿No encuentras lo que buscas?',
      ctaLink: 'Pregúntanos cualquier cosa o únete a nuestra comunidad en Telegram'
    },
    howItWorks: {
      title: 'Cómo funciona',
      steps: [{
        title: 'Contacto por Telegram',
        desc: 'Únete a nuestro grupo de Telegram o envíanos un mensaje directo. Toda la comunicación ocurre allí.'
      }, {
        title: 'Solicitar tu servicio',
        desc: 'Dinos lo que necesitas y la duración. Confirmamos disponibilidad, precio y tiempo.'
      }, {
        title: 'Configuración y asistencia',
        desc: 'Nos encargamos de la configuración, enviamos detalles de inicio de sesión seguros y te apoyamos hasta que todo funcione.'
      }]
    },
    whatYouGet: {
      title: '¿Qué obtienes?',
      subtitle: 'Deja de pagar precios minoristas por servicios que pueden obtenerse de manera más eficiente.',
      items: ['Precios privados (por debajo del comercio minorista público)', 'Entrega rápida (generalmente el mismo día)', 'Asistencia de configuración incluida', 'Comunicación directa (sin bots)', 'Garantía de devolución de dinero de 14 días'],
      guaranteeTitle: 'Garantía Férrea',
      guaranteeText: 'Tu satisfacción importa más que una transacción. Si surge algún problema dentro de los 14 días y no se puede resolver, recibes un reembolso completo.',
      guaranteeNote: 'Sin preguntas'
    },
    whyPrivate: {
      title: '¿Por qué acceso privado?',
      content: 'Paga menos. Totalmente privado. Trata con personas reales.',
      benefits: [{
        title: 'Más barato y rápido',
        desc: 'Mejores precios que el comercio. Configuración rápida.'
      }, {
        title: 'Privado y discreto',
        desc: 'Sin ID. Sin tarjetas. Sin exposición.'
      }, {
        title: 'Soporte humano real',
        desc: 'Sin bots. Sin tickets. Ayuda directa hasta que funcione.'
      }]
    },
    guarantee: {
      title: 'Garantía de Devolución de Dinero de 14 Días',
      content: 'Si surge algún problema dentro de los 14 días posteriores a la entrega y no se puede resolver, recibes un reembolso completo — sin discusiones.'
    },
    testimonials: {
      title: 'Comentarios reales de clientes',
      subtitle: 'Compartidos con permiso. Nombres difuminados por privacidad.',
      reviews: [{
        text: 'La configuración se hizo en menos de 30 minutos. Todo funcionó exactamente como se prometió.',
        date: 'Marzo 2024'
      }, {
        text: 'Me ahorré mucho comparado con el comercio minorista. El soporte fue directo y rápido.',
        date: 'Febrero 2024'
      }, {
        text: 'Cuenta entregada, contraseña cambiada, 2FA habilitado. Proceso fluido.',
        date: 'Marzo 2024'
      }, {
        text: 'Era escéptico al principio, pero funcionó perfectamente.',
        date: 'Enero 2024'
      }, {
        text: 'Tiempo de respuesta rápido e instrucciones claras. Lo usaría de nuevo.',
        date: 'Febrero 2024'
      }, {
        text: 'Sin complicaciones, sin retrasos. Exactamente lo que necesitaba.',
        date: 'Marzo 2024'
      }],
      clientLabel: 'Cliente de Telegram',
      disclaimer: 'Las reseñas se comparten con permiso. Detalles personales eliminados por privacidad.'
    },
    faq: {
      title: 'Preguntas Frecuentes',
      items: [{
        q: '¿Cómo funciona el servicio?',
        a: 'Nos contactas en Telegram, nos dices qué servicio necesitas y la duración. Confirmamos disponibilidad y precio, recibimos el pago, completamos la configuración y entregamos el acceso con soporte.'
      }, {
        q: '¿Qué métodos de pago aceptan?',
        a: 'Actualmente, solo aceptamos pagos en criptomonedas. Las criptomonedas son rápidas, seguras y permiten procesamiento instantáneo. Opciones de pago adicionales como PayPal, tarjetas y transferencias bancarias están planificadas y se agregarán pronto.'
      }, {
        q: '¿Qué tan rápido recibiré mi cuenta o configuración?',
        a: 'En la mayoría de los casos, la entrega toma de 10 minutos a 1 hora. En situaciones raras (retrasos de disponibilidad o verificación), la entrega puede tomar hasta 24 horas como máximo.'
      }, {
        q: '¿Cómo recibo los detalles de la cuenta?',
        a: 'Una vez completada la configuración, te enviamos el nombre de usuario y contraseña a través de un mensaje seguro de Telegram. Inicias sesión, cambias la contraseña, activas 2FA, y la cuenta se convierte completamente en tuya para usar.'
      }, {
        q: '¿La cuenta estará completamente bajo mi control?',
        a: 'Sí. Después del primer inicio de sesión, puedes cambiar datos de identificación, activar funciones de seguridad y usar la cuenta normalmente segun el plan que compraste.'
      }, {
        q: '¿Es este servicio oficial o está afiliado con las marcas?',
        a: 'No. Este es un servicio de asistencia de configuración y acceso de terceros. No estamos afiliados ni respaldados por ninguna marca o plataforma.'
      }, {
        q: '¿Está protegida mi privacidad?',
        a: 'Sí. La comunicación se maneja de forma privada a través de Telegram, y solo se solicita la información mínima requerida para la configuración. No se almacenan ni comparten datos innecesarios.'
      }, {
        q: '¿Qué sucede si hay un problema con mi cuenta?',
        a: 'Si ocurre algún problema, proporcionamos soporte directo. Si el problema no se puede resolver dentro de los 14 días, eres elegible para un reembolso completo bajo nuestra garantía de devolución de dinero.'
      }, {
        q: '¿Hay cargos recurrentes de su parte?',
        a: 'No. No cobramos tarifas recurrentes. Cualquier costo de suscripción o plan está relacionado solo con el servicio que solicitaste y acordaste.'
      }, {
        q: '¿Por qué los precios no están listados públicamente?',
        a: 'Los precios y la disponibilidad pueden cambiar según el servicio y la duración. Para garantizar precisión y acceso justo, todas las cotizaciones se proporcionan de forma privada a través de Telegram.'
      }]
    },
    finalCta: {
      title: '¿Listo para dejar de pagar de más?',
      button: 'Obtener acceso privado en Telegram'
    },
    footer: {
      disclaimer: 'Esta es una plataforma de asistencia de servicios digitales de terceros. No está afiliada con ningún proveedor de software o servicios. Todos los servicios se proporcionan privadamente con soporte de configuración y garantía de 14 días.',
      copyright: 'Private Digital Concierge. Todos los derechos reservados.'
    }
  },
  he: {
    hero: {
      headline: 'תפסיקו לשלם יותר מדי עבור שירותים דיגיטליים',
      subheadline: 'גישה פרטית, הגדרה מהירה ותמיכה אישית — ללא מחירים ציבוריים.',
      cta: 'קבל גישה פרטית בטלגרם',
      note: 'תקשורת ישירה. ללא בוטים.',
      servicesNote: 'גישה לשירותים דיגיטליים פרימיום במחירים פרטיים'
    },
    whatThisIs: {
      title: 'מה זה',
      content: 'אני עוזר לאנשים לקבל גישה לשירותים דיגיטליים באמצעות רכש פרטי, כולל סיוע בהגדרה ותמיכה, במחירים נמוכים יותר מהקמעונאות הסטנדרטית. הכל מטופל באופן אישי ופרטי דרך טלגרם.'
    },
    whatCanWeSetup: {
      title: 'מה אנחנו יכולים להגדיר עבורך',
      categories: [{
        title: 'תוכנה',
        subtitle: 'אפליקציות, מנויים, תמיכה בהגדרה'
      }, {
        title: 'כלי AI',
        subtitle: 'כלים פרימיום, הגדרה, הדרכה'
      }, {
        title: 'משחקים',
        subtitle: 'גישה, הגדרה, סיוע במשחק'
      }, {
        title: 'הגדרת חשבון',
        subtitle: 'הגדרה מאובטחת + מסירה'
      }],
      ctaText: 'לא מוצא את מה שאתה מחפש?',
      ctaLink: 'שאל אותנו כל דבר או הצטרף לקהילה שלנו בטלגרם'
    },
    howItWorks: {
      title: 'איך זה עובד',
      steps: [{
        title: 'יצירת קשר דרך טלגרם',
        desc: 'הצטרף לקבוצת הטלגרם שלנו או שלח לנו הודעה ישירה. כל התקשורת מתרחשת שם.'
      }, {
        title: 'בקש את השירות שלך',
        desc: 'ספר לנו מה אתה צריך ולכמה זמן. אנחנו מאשרים זמינות, מחיר וזמן.'
      }, {
        title: 'הגדרה וסיוע',
        desc: 'אנחנו מטפלים בהגדרה, שולחים פרטי התחברות מאובטחים ותומכים בך עד שהכל עובד.'
      }]
    },
    whatYouGet: {
      title: 'מה אתה מקבל',
      subtitle: 'תפסיק לשלם מחירי קמעונאות עבור שירותים שניתן להשיג בצורה יעילה יותר.',
      items: ['מחירים פרטיים (מתחת לקמעונאות ציבורית)', 'משלוח מהיר (בדרך כלל באותו היום)', 'סיוע בהגדרה כלול', 'תקשורת ישירה (ללא בוטים)', 'אחריות החזר כספי ל-14 ימים'],
      guaranteeTitle: 'אחריות ברזל',
      guaranteeText: 'שביעות הרצון שלך חשובה יותר מעסקה אחת. אם מתעוררת בעיה כלשהי תוך 14 יום ולא ניתן לפתור אותה, אתה מקבל החזר מלא.',
      guaranteeNote: 'ללא שאלות'
    },
    whyPrivate: {
      title: 'למה גישה פרטית?',
      content: 'שלם פחות. פרטי לחלוטין. התמודד עם אנשים אמיתיים.',
      benefits: [{
        title: 'זול ומהיר יותר',
        desc: 'מחירים טובים יותר מקמעונאות. הגדרות מהירות.'
      }, {
        title: 'פרטי ודיסקרטי',
        desc: 'ללא תעודה מזהה. ללא כרטיסי אשראי. ללא חשיפה.'
      }, {
        title: 'תמיכה אנושית אמיתית',
        desc: 'ללא בוטים. ללא כרטיסים. עזרה ישירה עד שזה עובד.'
      }]
    },
    guarantee: {
      title: 'אחריות החזר כספי ל-14 ימים',
      content: 'אם מתעוררת בעיה כלשהי תוך 14 יום מהמסירה ולא ניתן לפתור אותה, אתה מקבל החזר מלא — ללא ויכוחים.'
    },
    testimonials: {
      title: 'משוב אמיתי מלקוחות',
      subtitle: 'משותף באישור. שמות מטושטשים לפרטיות.',
      reviews: [{
        text: 'ההגדרה בוצעה תוך פחות מ-30 דקות. הכל עבד בדיוק כפי שהובטח.',
        date: 'מרץ 2024'
      }, {
        text: 'חסכתי הרבה בהשוואה לקמעונאות. התמיכה הייתה ישירה ומהירה.',
        date: 'פברואר 2024'
      }, {
        text: 'החשבון נמסר, הסיסמה שונתה, אימות דו-שלבי הופעל. תהליך חלק.',
        date: 'מרץ 2024'
      }, {
        text: 'הייתי סקפטי בהתחלה, אבל זה עבד בצורה מושלמת.',
        date: 'ינואר 2024'
      }, {
        text: 'זמן תגובה מהיר והוראות ברורות. הייתי משתמש שוב.',
        date: 'פברואר 2024'
      }, {
        text: 'ללא טרחה, ללא עיכובים. בדיוק מה שהייתי צריך.',
        date: 'מרץ 2024'
      }],
      clientLabel: 'לקוח טלגרם',
      disclaimer: 'ביקורות משותפות באישור. פרטים אישיים הוסרו לפרטיות.'
    },
    faq: {
      title: 'שאלות נפוצות',
      items: [{
        q: 'איך השירות עובד?',
        a: 'אתה יוצר איתנו קשר בטלגרם, אומר לנו איזה שירות אתה צריך ולכמה זמן. אנחנו מאשרים זמינות ומחיר, מקבלים תשלום, משלימים את ההגדרה ומוסרים גישה עם תמיכה.'
      }, {
        q: 'אילו אמצעי תשלום אתם מקבלים?',
        a: 'כרגע, אנחנו מקבלים תשלומי קריפטו בלבד. קריפטו הוא מהיר, מאובטח ומאפשר עיבוד מיידי. אפשרויות תשלום נוספות כמו PayPal, כרטיסים והעברות בנקאיות מתוכננות ויתווספו בקרוב.'
      }, {
        q: 'כמה מהר אקבל את החשבון או ההגדרה שלי?',
        a: 'ברוב המקרים, המסירה לוקחת 10 דקות עד שעה אחת. במצבים נדירים (עיכובי זמינות או אימות), המסירה יכולה לקחת עד 24 שעות לכל היותר.'
      }, {
        q: 'איך אני מקבל את פרטי החשבון?',
        a: 'לאחר השלמת ההגדרה, אנחנו שולחים לך את שם המשתמש והסיסמה דרך הודעת טלגרם מאובטחת. אתה מתחבר, משנה את הסיסמה, מפעיל אימות דו-שלבי, והחשבון הופך להיות שלך לחלוטין לשימוש.'
      }, {
        q: 'האם החשבון יהיה תחת השליטה המלאה שלי?',
        a: 'כן. לאחר ההתחברות הראשונה, אתה יכול לשנות פרטי גישה, להפעיל תכונות אבטחה ולהשתמש בחשבון בדרך רגילה לפי התוכנית שרכשת.'
      }, {
        q: 'האם השירות הזה רשמי או מזוהה עם המותגים?',
        a: 'לא. זהו שירות סיוע בהגדרה וגישה של צד שלישי. אנחנו לא מזוהים או נתמכים על ידי שום מותג או פלטפורמה.'
      }, {
        q: 'האם הפרטיות שלי מוגנת?',
        a: 'כן. התקשורת מטופלת באופן פרטי דרך טלגרם, ורק המידע המינימלי הנדרש להגדרה מתבקש. אין נתונים מיותרים שנשמרים או משותפים.'
      }, {
        q: 'מה קורה אם יש בעיה עם החשבון שלי?',
        a: 'אם מתרחשת בעיה כלשהי, אנחנו מספקים תמיכה ישירה. אם לא ניתן לפתור את הבעיה תוך 14 יום, אתה זכאי להחזר מלא תחת אחריות ההחזר הכספי שלנו.'
      }, {
        q: 'האם יש חיובים חוזרים מצדכם?',
        a: 'לא. אנחנו לא גובים עמלות חוזרות. כל עלות מנוי או תוכנית קשורה רק לשירות שביקשת והסכמת עליו.'
      }, {
        q: 'למה המחירים לא מפורטים בפומבי?',
        a: 'המחירים והזמינות יכולים להשתנות בהתאם לשירות ולמשך הזמן. כדי להבטיח דיוק וגישה הוגנת, כל הצעות המחיר מסופקות באופן פרטי דרך טלגרם.'
      }]
    },
    finalCta: {
      title: 'מוכן להפסיק לשלם יותר מדי?',
      button: 'קבל גישה פרטית בטלגרם'
    },
    footer: {
      disclaimer: 'זוהי פלטפורמת סיוע לשירותים דיגיטליים של צד שלישי. לא מזוהה עם ספקי תוכנה או שירותים כלשהם. כל השירותים מסופקים באופן פרטי עם תמיכה בהגדרה ואחריות ל-14 יום.',
      copyright: 'Private Digital Concierge. כל הזכויות שמורות.'
    }
  }
};
const languageOptions = [{
  code: 'es' as Language,
  name: 'Español',
  flag: '🇪🇸'
}, {
  code: 'en' as Language,
  name: 'English',
  flag: '🇬🇧'
}, {
  code: 'de' as Language,
  name: 'Deutsch',
  flag: '🇩🇪'
}, {
  code: 'he' as Language,
  name: 'עברית',
  flag: '🇮🇱'
}];
export function LandingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  // Set video playback speed and handle loop fading
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    // Keep the slow motion effect
    video.playbackRate = 0.5;
    let animationFrameId: number;
    const updateOpacity = () => {
      const duration = video.duration;
      const currentTime = video.currentTime;
      if (duration) {
        const fadeTime = 1.5; // Duration of fade in video time
        const maxOpacity = 0.5; // Target max opacity
        let newOpacity = maxOpacity;
        if (currentTime < fadeTime) {
          // Fade in at start
          newOpacity = currentTime / fadeTime * maxOpacity;
        } else if (currentTime > duration - fadeTime) {
          // Fade out at end
          newOpacity = (duration - currentTime) / fadeTime * maxOpacity;
        }
        video.style.opacity = String(Math.max(0, newOpacity));
      }
      animationFrameId = requestAnimationFrame(updateOpacity);
    };
    // Start the animation loop
    updateOpacity();
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, []);
  // Detect user's browser language and set initial language
  const detectBrowserLanguage = (): Language => {
    const browserLang = navigator.language || navigator.languages?.[0] || 'en';
    const langCode = browserLang.toLowerCase().split('-')[0];
    // Map to our supported languages
    if (langCode === 'de') return 'de';
    if (langCode === 'es') return 'es';
    if (langCode === 'he' || langCode === 'iw') return 'he'; // 'iw' is old Hebrew code
    return 'en'; // Default to English
  };
  const [language, setLanguage] = useState<Language>(detectBrowserLanguage());
  const t = translations[language];
  const currentLanguage = languageOptions.find(lang => lang.code === language);
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const changeLanguage = (lang: Language) => {
    setLanguage(lang);
    setShowLanguageMenu(false);
  };
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <div className="min-h-screen bg-white text-gray-900 font-sans selection:bg-gray-200 relative overflow-hidden" dir={language === 'he' ? 'rtl' : 'ltr'} data-id="element-69">
      {/* Video Background */}
      <div className="fixed inset-0 z-0 pointer-events-none" data-id="element-70">
        <video ref={videoRef} autoPlay loop muted playsInline className="w-full h-full object-cover transform-gpu transition-opacity duration-75 contrast-125 saturate-110" style={{
        opacity: 0
      }} data-id="element-71">
          <source src="https://cdn.magicpatterns.com/uploads/ma3TdJ3KQrhJic1gyQDpaE/Animate_it_to_202601162126_gq6ay.undefined" type="video/mp4" data-id="element-72" />
        </video>
        {/* White overlay to ensure text readability if needed, but keeping it minimal as requested */}
        <div className="absolute inset-0 bg-white/10" data-id="element-73" />
      </div>

      {/* Content wrapper */}
      <div className="relative z-10" data-id="element-74">
        {/* 1. Hero Section */}
        <section className="pt-24 pb-16 px-6 md:pt-32 md:pb-24 max-w-4xl mx-auto text-center" data-id="element-75">
          {/* Logo with floating brand icons */}
          <div className="mb-16 relative" data-id="element-76">
            <div className="relative inline-block mb-12" data-id="element-77">
              {/* Splat-shaped background for main logo */}
              <div className="absolute inset-0 bg-gradient-to-br from-green-400 via-emerald-500 to-teal-500 opacity-20 blur-sm" style={{
              clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
              transform: 'scale(1.1)'
            }} data-id="element-78"></div>

              <img src="https://cdn.magicpatterns.com/uploads/nz9fiQTvKLa84b5PZU6PUa/IMG_9833_GOOD_upscayl_2x_digital-art-4x.png" alt="OffGrid Access" className="h-48 md:h-64 mx-auto relative z-10" data-id="element-79" />

              {/* Floating Brand Icons */}
              <div className="absolute inset-0 pointer-events-none z-20" data-id="element-80">
                {/* ChatGPT - Top Left */}
                <div className="absolute -top-4 -left-12 md:-left-20 w-12 h-12 md:w-16 md:h-16 bg-white rounded-xl shadow-lg flex items-center justify-center border border-gray-100 animate-orbit" style={{
                animationDelay: '0s'
              }} data-id="element-81">
                  <svg className="w-6 h-6 md:w-8 md:h-8" viewBox="0 0 24 24" fill="none" data-id="element-82">
                    <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" fill="#10A37F" data-id="element-83" />
                  </svg>
                </div>

                {/* Netflix - Top Right */}
                <div className="absolute -top-4 -right-12 md:-right-20 w-12 h-12 md:w-16 md:h-16 bg-black rounded-xl shadow-lg flex items-center justify-center animate-orbit" style={{
                animationDelay: '1s'
              }} data-id="element-84">
                  <span className="text-red-600 font-bold text-xl md:text-2xl" data-id="element-85">
                    N
                  </span>
                </div>

                {/* Google AI - Left (Wrapped for translate) */}
                <div className="absolute top-1/2 -translate-y-1/2 -left-16 md:-left-24" data-id="element-86">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-white rounded-xl shadow-lg flex items-center justify-center border border-gray-100 animate-orbit" style={{
                  animationDelay: '2s'
                }} data-id="element-87">
                    <svg className="w-6 h-6 md:w-8 md:h-8" viewBox="0 0 24 24" data-id="element-88">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" data-id="element-89" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" data-id="element-90" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" data-id="element-91" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" data-id="element-92" />
                    </svg>
                  </div>
                </div>

                {/* HBO - Right (Wrapped for translate) */}
                <div className="absolute top-1/2 -translate-y-1/2 -right-16 md:-right-24" data-id="element-93">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-purple-700 rounded-xl shadow-lg flex items-center justify-center animate-orbit" style={{
                  animationDelay: '3s'
                }} data-id="element-94">
                    <span className="text-white font-bold text-sm md:text-base" data-id="element-95">
                      HBO
                    </span>
                  </div>
                </div>

                {/* Spotify - Bottom Left */}
                <div className="absolute -bottom-4 -left-12 md:-left-20 w-12 h-12 md:w-16 md:h-16 bg-green-500 rounded-xl shadow-lg flex items-center justify-center animate-orbit" style={{
                animationDelay: '4s'
              }} data-id="element-96">
                  <svg className="w-6 h-6 md:w-8 md:h-8" viewBox="0 0 24 24" fill="white" data-id="element-97">
                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" data-id="element-98" />
                  </svg>
                </div>

                {/* YouTube Premium - Bottom Right */}
                <div className="absolute -bottom-4 -right-12 md:-right-20 w-12 h-12 md:w-16 md:h-16 bg-red-600 rounded-xl shadow-lg flex items-center justify-center animate-orbit" style={{
                animationDelay: '5s'
              }} data-id="element-99">
                  <svg className="w-6 h-6 md:w-8 md:h-8" viewBox="0 0 24 24" fill="white" data-id="element-100">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" data-id="element-101" />
                  </svg>
                </div>

                {/* CapCut - Top Inner Left */}
                <div className="absolute -top-16 left-2 md:-top-24 md:left-6 w-12 h-12 md:w-16 md:h-16 bg-black rounded-xl shadow-lg flex items-center justify-center overflow-hidden border border-gray-800 animate-orbit" style={{
                animationDelay: '1.5s'
              }} data-id="element-102">
                  <img src="https://cdn.magicpatterns.com/uploads/5jnVWcEJqyDFjc27vwXq3y/CapCut.jpg" alt="CapCut" className="w-full h-full object-cover" data-id="element-103" />
                </div>

                {/* Eleven Labs - Top Inner Right */}
                <div className="absolute -top-16 right-2 md:-top-24 md:right-6 w-12 h-12 md:w-16 md:h-16 bg-white rounded-xl shadow-lg flex items-center justify-center overflow-hidden border border-gray-100 animate-orbit" style={{
                animationDelay: '2.5s'
              }} data-id="element-104">
                  <img src="https://cdn.magicpatterns.com/uploads/7dCkkFrS6FKsYPSuC59eQr/eleven_labs.png" alt="Eleven Labs" className="w-full h-full object-cover" data-id="element-105" />
                </div>

                {/* Tinder - Bottom Inner Left */}
                <div className="absolute -bottom-16 left-2 md:-bottom-24 md:left-6 w-12 h-12 md:w-16 md:h-16 bg-white rounded-xl shadow-lg flex items-center justify-center overflow-hidden border border-gray-100 animate-orbit" style={{
                animationDelay: '3.5s'
              }} data-id="element-106">
                  <img src="https://cdn.magicpatterns.com/uploads/numCb1ocp2jxMTCATJj5Lx/Tinder.jpg" alt="Tinder" className="w-full h-full object-cover" data-id="element-107" />
                </div>

                {/* Adobe - Bottom Inner Right */}
                <div className="absolute -bottom-16 right-2 md:-bottom-24 md:right-6 w-12 h-12 md:w-16 md:h-16 bg-white rounded-xl shadow-lg flex items-center justify-center overflow-hidden border border-gray-100 animate-orbit" style={{
                animationDelay: '4.5s'
              }} data-id="element-108">
                  <img src="https://cdn.magicpatterns.com/uploads/ed6tKgEacaKLyWUu1FunLX/Adobe.jpg" alt="Adobe" className="w-full h-full object-cover" data-id="element-109" />
                </div>

                {/* Steam - Top Center (Wrapped for translate) */}
                <div className="absolute -top-20 left-1/2 -translate-x-1/2" data-id="element-110">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-white rounded-xl shadow-lg flex items-center justify-center overflow-hidden border border-gray-100 animate-orbit" style={{
                  animationDelay: '0.5s'
                }} data-id="element-111">
                    <img src="https://cdn.magicpatterns.com/uploads/msJvsR7JHVdJFAMYHTC69B/steam.jpg" alt="Steam" className="w-full h-full object-cover" data-id="element-112" />
                  </div>
                </div>

                {/* Perplexity - Bottom Center (Wrapped for translate) */}
                <div className="absolute -bottom-20 left-1/2 -translate-x-1/2" data-id="element-113">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-white rounded-xl shadow-lg flex items-center justify-center overflow-hidden border border-gray-100 animate-orbit" style={{
                  animationDelay: '5.5s'
                }} data-id="element-114">
                    <img src="https://cdn.magicpatterns.com/uploads/66KCMdFFPHZqUjmVE7g9jD/Perplexity.png" alt="Perplexity" className="w-full h-full object-cover" data-id="element-115" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Language Dropdown - Dark style like image */}
          <div className="mb-8 relative inline-block" data-id="element-116">
            <button onClick={() => setShowLanguageMenu(!showLanguageMenu)} className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-full shadow-lg hover:bg-gray-800 transition-colors min-w-[140px]" data-id="element-117">
              <span className="text-xl" data-id="element-118">{currentLanguage?.flag}</span>
              <span className="text-xs font-medium flex-1 text-left" data-id="element-119">
                {currentLanguage?.name}
              </span>
              {showLanguageMenu ? <ChevronUp className="w-3 h-3 text-gray-400" data-id="element-120" /> : <ChevronDown className="w-3 h-3 text-gray-400" data-id="element-121" />}
            </button>

            {showLanguageMenu && <div className="absolute top-full mt-2 left-0 w-full bg-gray-900 rounded-2xl shadow-2xl overflow-hidden z-50 border border-gray-800" data-id="element-122">
                {languageOptions.map(lang => <button key={lang.code} onClick={() => changeLanguage(lang.code)} className={`w-full flex items-center gap-2 px-4 py-2 text-white hover:bg-gray-800 transition-colors ${language === lang.code ? 'bg-gray-800' : ''}`} data-id="element-123">
                    <span className="text-xl" data-id="element-124">{lang.flag}</span>
                    <span className="text-xs font-medium" data-id="element-125">{lang.name}</span>
                  </button>)}
              </div>}
          </div>

          {/* Hero Content with Glass Background */}
          <div className="bg-white/60 backdrop-blur-md rounded-3xl p-8 shadow-sm border border-white/40 mb-10" data-id="element-126">
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 leading-tight text-gray-900" data-id="element-127">
              {t.hero.headline.split(' ').slice(0, 3).join(' ')}{' '}
              <br className="hidden md:block" data-id="element-128" />
              {t.hero.headline.split(' ').slice(3).join(' ')}
            </h1>
            <p className="text-xl md:text-2xl text-gray-800 mb-0 leading-relaxed max-w-2xl mx-auto font-normal" data-id="element-129">
              {t.hero.subheadline}
            </p>
          </div>

          <a href="https://t.me/offgridaccess" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white rounded-lg transition-colors shadow-sm hover:shadow-md w-full md:w-auto relative overflow-hidden" style={{
          backgroundColor: '#0088cc'
        }} data-id="element-130">
            {/* Telegram logo background */}
            <svg className="absolute right-2 top-1/2 -translate-y-1/2 opacity-20 pointer-events-none" width="80" height="80" viewBox="0 0 24 24" fill="white" data-id="element-131">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" data-id="element-132" />
            </svg>
            <MessageCircle className="w-5 h-5 mr-2 relative z-10" data-id="element-133" />
            <span className="relative z-10" data-id="element-134">{t.hero.cta}</span>
          </a>
          <p className="mt-4 text-sm text-gray-600 font-medium bg-white/50 inline-block px-3 py-1 rounded-full" data-id="element-135">
            {t.hero.note}
          </p>
        </section>

        {/* 2. What this is */}
        <section className="py-16 px-6 bg-white/90 backdrop-blur-md border-y border-gray-100/50" data-id="element-136">
          <div className="max-w-2xl mx-auto text-center" data-id="element-137">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-138">
              {t.whatThisIs.title}
            </h2>
            <p className="text-lg md:text-xl text-gray-800 leading-relaxed" data-id="element-139">
              {t.whatThisIs.content}
            </p>
          </div>
        </section>

        {/* 3. How it works */}
        <section className="py-20 px-6 max-w-5xl mx-auto" data-id="element-140">
          <h2 className="text-3xl font-bold text-center mb-16 tracking-tight bg-white/60 backdrop-blur-sm inline-block px-8 py-2 rounded-2xl mx-auto" data-id="element-141">
            {t.howItWorks.title}
          </h2>
          <div className="grid md:grid-cols-3 gap-12 relative" data-id="element-142">
            <div className="hidden md:block absolute top-12 left-0 w-full h-0.5 bg-gray-200/50 -z-10" data-id="element-143"></div>

            {t.howItWorks.steps.map((item, idx) => <div key={idx} className="flex flex-col items-center text-center bg-white/95 backdrop-blur-md rounded-xl p-6 shadow-sm border border-white/50" data-id="element-144">
                <div className="mb-6" data-id="element-145">
                  {idx === 0 &&
              // Telegram Logo - Original Blue
              <svg className="w-16 h-16" viewBox="0 0 240 240" fill="none" data-id="element-146">
                      <defs data-id="element-147">
                        <linearGradient id="telegramGradient" x1="120" y1="0" x2="120" y2="240" data-id="element-148">
                          <stop offset="0%" style={{
                      stopColor: '#2AABEE'
                    }} data-id="element-149" />
                          <stop offset="100%" style={{
                      stopColor: '#229ED9'
                    }} data-id="element-150" />
                        </linearGradient>
                      </defs>
                      <circle cx="120" cy="120" r="120" fill="url(#telegramGradient)" data-id="element-151" />
                      <path d="M81.229 128.772l14.237 39.406s1.78 3.687 3.686 3.687 30.255-29.492 30.255-29.492l31.525-60.89L81.737 118.6" fill="#C8DAEA" data-id="element-152" />
                      <path d="M100.106 138.878l-2.733 29.046s-1.144 8.9 7.754 0 17.415-15.763 17.415-15.763" fill="#A9C6D8" data-id="element-153" />
                      <path d="M81.486 130.178l-17.8-5.467s-2.006-.8-1.356-2.617c.145-.403.428-.72.857-1.03 4.12-2.98 76.68-29.035 76.68-29.035s1.634-.546 2.76-.36c.468.078.866.283 1.14.685.155.226.235.45.285.76.035.22.04.48.03.81-.123 4.15-11.468 51.733-11.468 51.733s-.67 2.55-3.1 2.633c-.985.033-2.206-.36-3.883-1.463-4.8-3.15-21.24-15.17-24.82-17.62-.48-.33-.96-.84-.47-1.43.91-1.1 20.48-18.45 31.88-29.09 1.02-.96.47-1.54-.6-.87-15.2 9.65-37.48 24.8-39.87 26.33-.83.53-1.63.64-2.4.46z" fill="white" data-id="element-154" />
                    </svg>}
                  {idx === 1 &&
              // Hand/Request Icon - Colorful
              <svg className="w-16 h-16" viewBox="0 0 64 64" fill="none" data-id="element-155">
                      <defs data-id="element-156">
                        <linearGradient id="handGradient" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-157">
                          <stop offset="0%" style={{
                      stopColor: '#FF6B6B'
                    }} data-id="element-158" />
                          <stop offset="50%" style={{
                      stopColor: '#FF8E53'
                    }} data-id="element-159" />
                          <stop offset="100%" style={{
                      stopColor: '#FFA94D'
                    }} data-id="element-160" />
                        </linearGradient>
                      </defs>
                      {/* Clipboard/Document */}
                      <rect x="12" y="8" width="40" height="48" rx="4" fill="#E3F2FD" stroke="#2196F3" strokeWidth="2" data-id="element-161" />
                      <rect x="18" y="4" width="28" height="8" rx="2" fill="#2196F3" data-id="element-162" />
                      {/* Checkmarks */}
                      <path d="M20 22l4 4 8-8" stroke="url(#handGradient)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-163" />
                      <line x1="20" y1="34" x2="44" y2="34" stroke="#90CAF9" strokeWidth="3" strokeLinecap="round" data-id="element-164" />
                      <line x1="20" y1="42" x2="38" y2="42" stroke="#90CAF9" strokeWidth="3" strokeLinecap="round" data-id="element-165" />
                      <line x1="20" y1="50" x2="44" y2="50" stroke="#90CAF9" strokeWidth="3" strokeLinecap="round" data-id="element-166" />
                    </svg>}
                  {idx === 2 &&
              // PC/Setup Icon - Colorful
              <svg className="w-16 h-16" viewBox="0 0 64 64" fill="none" data-id="element-167">
                      <defs data-id="element-168">
                        <linearGradient id="pcGradient" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-169">
                          <stop offset="0%" style={{
                      stopColor: '#667EEA'
                    }} data-id="element-170" />
                          <stop offset="100%" style={{
                      stopColor: '#764BA2'
                    }} data-id="element-171" />
                        </linearGradient>
                      </defs>
                      {/* Monitor */}
                      <rect x="8" y="10" width="48" height="32" rx="3" fill="url(#pcGradient)" data-id="element-172" />
                      <rect x="12" y="14" width="40" height="24" rx="1" fill="#1E293B" data-id="element-173" />
                      {/* Screen content - checkmark */}
                      <path d="M24 26l6 6 10-10" stroke="#4ADE80" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-174" />
                      {/* Monitor stand */}
                      <rect x="28" y="42" width="8" height="6" rx="1" fill="#94A3B8" data-id="element-175" />
                      <rect x="20" y="48" width="24" height="3" rx="1.5" fill="#CBD5E1" data-id="element-176" />
                      {/* Keyboard suggestion */}
                      <rect x="14" y="54" width="36" height="8" rx="2" fill="#E2E8F0" stroke="#94A3B8" strokeWidth="1" data-id="element-177" />
                      <line x1="18" y1="58" x2="22" y2="58" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" data-id="element-178" />
                      <line x1="26" y1="58" x2="38" y2="58" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" data-id="element-179" />
                      <line x1="42" y1="58" x2="46" y2="58" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" data-id="element-180" />
                    </svg>}
                </div>
                <span className="text-sm font-bold text-gray-400 mb-2" data-id="element-181">
                  STEP 0{idx + 1}
                </span>
                <h3 className="text-xl font-bold mb-3" data-id="element-182">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-183">{item.desc}</p>
              </div>)}
          </div>
        </section>

        {/* 3.5. What can we set up for you */}
        <WhatCanWeSetupSection title={t.whatCanWeSetup.title} categories={t.whatCanWeSetup.categories.map((cat, idx) => ({
        title: cat.title,
        subtitle: cat.subtitle,
        icon: Object.values(categoryIcons)[idx],
        href: 'https://t.me/offgridaccess'
      }))} ctaText={t.whatCanWeSetup.ctaText} ctaLink={t.whatCanWeSetup.ctaLink} data-id="element-184" />

        {/* 4. What you get */}
        <section className="py-20 px-6 bg-gray-900/90 backdrop-blur-md text-white" data-id="element-185">
          <div className="max-w-4xl mx-auto" data-id="element-186">
            <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-187">
              <div data-id="element-188">
                <h2 className="text-3xl font-bold mb-6" data-id="element-189">
                  {t.whatYouGet.title}
                </h2>
                <p className="text-gray-400 mb-8 text-lg" data-id="element-190">
                  {t.whatYouGet.subtitle}
                </p>
                <ul className="space-y-4" data-id="element-191">
                  {t.whatYouGet.items.map((item, idx) => <li key={idx} className="flex items-start" data-id="element-192">
                      <Check className="w-5 h-5 text-green-400 mr-3 mt-1 flex-shrink-0" data-id="element-193" />
                      <span className="text-gray-200 text-lg" data-id="element-194">{item}</span>
                    </li>)}
                </ul>
              </div>
              <div className="bg-gray-800/80 backdrop-blur-sm p-8 rounded-2xl border border-gray-700/50" data-id="element-195">
                <div className="flex items-center mb-6" data-id="element-196">
                  <ShieldCheck className="w-8 h-8 text-white mr-3" data-id="element-197" />
                  <h3 className="text-xl font-bold" data-id="element-198">
                    {t.whatYouGet.guaranteeTitle}
                  </h3>
                </div>
                <p className="text-gray-300 mb-6 leading-relaxed" data-id="element-199">
                  {t.whatYouGet.guaranteeText}
                </p>
                <div className="text-sm text-gray-400 font-medium uppercase tracking-wider" data-id="element-200">
                  {t.whatYouGet.guaranteeNote}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 5. Why private access */}
        <section className="py-20 px-6 max-w-3xl mx-auto text-center" data-id="element-201">
          <h2 className="text-3xl font-bold mb-8 bg-white/60 backdrop-blur-sm inline-block px-6 py-2 rounded-2xl" data-id="element-202">
            {t.whyPrivate.title}
          </h2>
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 mb-10" data-id="element-203">
            <p className="text-lg text-gray-800 leading-relaxed" data-id="element-204">
              {t.whyPrivate.content}
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-6 text-left" data-id="element-205">
            {t.whyPrivate.benefits.map((item, idx) => <div key={idx} className="relative p-6 bg-white/95 backdrop-blur-md rounded-xl overflow-hidden group shadow-sm border border-white/50" data-id="element-206">
                {/* Animated border effect */}
                <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-207">
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-gray-300 via-white to-gray-300 animate-[spin_3s_linear_infinite]" style={{
                padding: '2px'
              }} data-id="element-208">
                    <div className="w-full h-full bg-white rounded-xl" data-id="element-209"></div>
                  </div>
                </div>

                {/* Static border */}
                <div className="absolute inset-0 rounded-xl border border-gray-200 group-hover:border-transparent transition-colors duration-500" data-id="element-210"></div>

                {/* Content */}
                <div className="relative z-10" data-id="element-211">
                  <h3 className="font-bold text-gray-900 mb-2" data-id="element-212">{item.title}</h3>
                  <p className="text-sm text-gray-600" data-id="element-213">{item.desc}</p>
                </div>
              </div>)}
          </div>
        </section>

        {/* 6. Guarantee Section */}
        <section className="py-16 px-6 bg-white/90 backdrop-blur-md border-y border-gray-200/50" data-id="element-214">
          <div className="max-w-3xl mx-auto text-center" data-id="element-215">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full shadow-sm mb-6 text-gray-900" data-id="element-216">
              <ShieldCheck className="w-8 h-8" data-id="element-217" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold mb-4" data-id="element-218">
              {t.guarantee.title}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-219">
              {t.guarantee.content}
            </p>
          </div>
        </section>

        {/* 6.5. Testimonials Section */}
        <section className="py-20 px-6 bg-gray-900/95 backdrop-blur-md" data-id="element-220">
          <div className="max-w-6xl mx-auto" data-id="element-221">
            <div className="text-center mb-12" data-id="element-222">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3" data-id="element-223">
                {t.testimonials.title}
              </h2>
              <p className="text-gray-400 text-sm" data-id="element-224">{t.testimonials.subtitle}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8" data-id="element-225">
              {t.testimonials.reviews.map((review, idx) => <div key={idx} className="bg-gray-800/90 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-6 flex flex-col" data-id="element-226">
                  {/* Star Rating */}
                  <div className="flex gap-1 mb-4" data-id="element-227">
                    {[...Array(5)].map((_, i) => <svg key={i} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20" data-id="element-228">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-229" />
                      </svg>)}
                  </div>

                  {/* Review Text */}
                  <p className="text-gray-200 leading-relaxed mb-4 flex-grow" data-id="element-230">
                    "{review.text}"
                  </p>

                  {/* Footer */}
                  <div className="flex items-center justify-between text-xs" data-id="element-231">
                    <span className="text-gray-500 font-medium" data-id="element-232">
                      {t.testimonials.clientLabel}
                    </span>
                    <span className="text-gray-600" data-id="element-233">{review.date}</span>
                  </div>
                </div>)}
            </div>

            {/* Disclaimer */}
            <p className="text-center text-xs text-gray-500" data-id="element-234">
              {t.testimonials.disclaimer}
            </p>
          </div>
        </section>

        {/* 7. FAQ Section */}
        <section className="py-20 px-6 max-w-3xl mx-auto" data-id="element-235">
          <h2 className="text-3xl font-bold text-center mb-12 bg-white/60 backdrop-blur-sm inline-block px-8 py-2 rounded-2xl mx-auto block w-fit" data-id="element-236">
            {t.faq.title}
          </h2>
          <div className="space-y-4" data-id="element-237">
            {t.faq.items.map((faq, idx) => <div key={idx} className="border border-gray-200/50 rounded-lg overflow-hidden backdrop-blur-sm" data-id="element-238">
                <button onClick={() => toggleFaq(idx)} className="w-full flex justify-between items-center p-5 text-left bg-white/90 hover:bg-white transition-colors" data-id="element-239">
                  <span className="font-medium text-gray-900" data-id="element-240">{faq.q}</span>
                  {openFaq === idx ? <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0 ml-4" data-id="element-241" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0 ml-4" data-id="element-242" />}
                </button>
                {openFaq === idx && <div className="p-5 pt-0 text-gray-600 bg-white/90 border-t border-gray-100/50" data-id="element-243">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </section>

        {/* 8. Final Call to Action */}
        <section className="py-24 px-6 text-center" data-id="element-244">
          <div className="bg-white/60 backdrop-blur-md rounded-3xl p-10 max-w-3xl mx-auto" data-id="element-245">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 tracking-tight" data-id="element-246">
              {t.finalCta.title}
            </h2>
            <a href="https://t.me/offgridaccess" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white rounded-lg transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 relative overflow-hidden" style={{
            backgroundColor: '#0088cc'
          }} data-id="element-247">
              {/* Telegram logo background */}
              <svg className="absolute right-2 top-1/2 -translate-y-1/2 opacity-20 pointer-events-none" width="80" height="80" viewBox="0 0 24 24" fill="white" data-id="element-248">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" data-id="element-249" />
              </svg>
              <MessageCircle className="w-5 h-5 mr-2 relative z-10" data-id="element-250" />
              <span className="relative z-10" data-id="element-251">{t.finalCta.button}</span>
            </a>
          </div>
        </section>

        {/* 9. Footer */}
        <footer className="py-12 px-6 border-t border-gray-200/50 bg-white/90 backdrop-blur-md" data-id="element-252">
          <div className="max-w-3xl mx-auto text-center" data-id="element-253">
            <p className="text-xs text-gray-500 leading-relaxed mb-4" data-id="element-254">
              {t.footer.disclaimer}
            </p>
            <p className="text-xs text-gray-400" data-id="element-255">
              &copy; {new Date().getFullYear()} {t.footer.copyright}
            </p>
          </div>
        </footer>
      </div>
    </div>;
}